<template>
    <PageLoading v-if="isLoading" />
    <div v-else class="q-pa-md my-courses">
        <!-- filters -->
        <div class="row q-col-gutter-md">
            <!-- course types -->
            <q-select
                v-model="filter.courseType"
                class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-grow"
                square
                outlined
                dense
                clearable
                :options="courseTypesOptions"
                :label="$t('general.course_type')"
                option-value="id"
                :option-label="(option) => JSON.parse(option.title)[locale]"
                emit-value
                map-options
                bg-color="white"
                @input="getCourses"
            />
            <!-- enrollment status  -->
            <q-select
                v-model="filter.enrollmentStatus"
                class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-grow"
                square
                outlined
                dense
                clearable
                :options="enrollmentsStatusOptions"
                :label="$t('general.enrollment_status')"
                option-value="id"
                :option-label="
                    (option) => JSON.parse(option.title)[locale]
                "
                emit-value
                map-options
                bg-color="white"
                :disable="!onsiteTypeSelected"
                @input="getCourses"
            />
        </div>
        <template v-if="courses.length">
            <!-- courses library -->
            <div class="row q-col-gutter-lg q-mt-md">
                <CourseLibraryThumb
                    v-for="course in courses"
                    :key="course.id"
                    :course="course"
                    :user-role="user.userRoles.name"
                >
                </CourseLibraryThumb>
            </div>
            <!-- pagination -->
            <div class="q-pa-lg flex flex-center">
                <q-pagination
                    v-if="pagination.max != 0"
                    v-model="pagination.page"
                    color="secondary"
                    :max="pagination.max"
                    :max-pages="6"
                    :direction-links="true"
                    :boundary-links="true"
                    :boundary-numbers="true"
                    @input="fetchStudentCourses"
                ></q-pagination>
            </div>
        </template>
        <template v-else>
            <div class="row q-col-gutter-lg q-mt-md">
                <div class="col-12">
                    <h6>{{ $t('profile.courses_none') }}</h6>
                    <q-btn class="q-my-md" to="/courses" color="secondary">
                        {{ $t('general.view_course_catalog') }}
                    </q-btn>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import PageLoading from '@/components/PageLoading'
import librariesService from '@/services/modules/libraries'
import CourseLibraryThumb from '@/components/CourseLibraryThumb'
import courseTypesService from '@/services/modules/courseTypes'
import enrollmentStatusService from '@/services/modules/enrollmentsStatus'
import store from '@/store'
import { slugs } from '@/local-shared/mixins/slugs'

const loadingKeys = {
    fetchingStudentCourses: 'fetching-student-courses',
    fetchingCourseTypes: 'fetching-course-types',
    fetchingEnrollmentsStatus: 'fetching-enrollments-status',
}

export default {
    name: 'MyCourses',

    components: {
        PageLoading,
        CourseLibraryThumb,
    },

    data() {
        return {
            slugs: slugs,

            currentPage: 1,

            courses: [],
            courseTypesOptions: [],
            enrollmentsStatusOptions: [],

            pagination: {
                max: 1,
                page: 1,
                rowsPerPage: 6,
                rowsNumber: 0,
            },

            filter: {
                courseType: null,
                enrollmentStatus: null,
            },
        }
    },

    computed: {
        isLoading() {
            return this.isFetchingStudentCourses
        },

        isFetchingStudentCourses() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingStudentCourses
            ).length
        },

        isFetchingCourseTypes() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingCourseTypes
            ).length
        },

        user() {
            return store.getters['session/user']
        },

        locale() {
            return this.$i18n.locale
        },

        onlineTypeSelected() {
            let onlineType = this.courseTypesOptions.filter(
                (courseType) =>
                    courseType.slug === this.slugs.courseTypes.online
            )

            return onlineType.length
                ? this.filter.courseType === onlineType[0].id
                : false
        },

        onsiteTypeSelected() {
            let onsiteType = this.courseTypesOptions.filter(
                (courseType) =>
                    courseType.slug === this.slugs.courseTypes.onsite
            )

            return onsiteType.length
                ? this.filter.courseType === onsiteType[0].id
                : false
        },
    },

    async created() {
        this.fetchCoursesTypes()
        this.fetchEnrollmentsStatus()
        await this.fetchStudentCourses()
    },

    methods: {
        constructPagination() {
            return {
                perPage: this.pagination.rowsPerPage,
                currentPage: this.pagination.page,
            }
        },

        async fetchStudentCourses() {
            this.loadingAdd(loadingKeys.fetchingStudentCourses)

            let parameters = {
                filter: {
                    user: this.user.id,
                    courseTypeId: this.filter.courseType,
                    enrollmentStatusId: this.filter.enrollmentStatus,
                },
                pagination: this.constructPagination(),
                orderBy: [
                    {
                        column: 'created_at',
                        direction: 'desc',
                    },
                ],
                relations: {
                    enrollment: true,
                },
            }

            let response = await librariesService.retrieve(parameters)

            if (response.status === 200) {
                this.courses = response.data.results
                this.pagination.max = Math.ceil(
                    response.data.pagination.total /
                        response.data.pagination.itemsPerPage
                )
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('notify.error_occurred', {
                        message: response.data.message,
                    }),
                })
            }

            this.loadingRemove(loadingKeys.fetchingStudentCourses)
        },

        async fetchCoursesTypes() {
            this.loadingAdd(loadingKeys.fetchingCourseTypes)

            let parameters = {}

            let response = await courseTypesService.retrieve(parameters)

            if (response.status === 200) {
                this.courseTypesOptions = response.data.results
            }

            this.loadingRemove(loadingKeys.fetchingCourseTypes)
        },

        async fetchEnrollmentsStatus() {
            this.loadingAdd(loadingKeys.fetchingEnrollmentsStatus)

            let parameters = {
                filter: {},
            }

            let response = await enrollmentStatusService.retrieve(parameters)

            if (response.status === 200) {
                this.enrollmentsStatusOptions = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingEnrollmentsStatus)
        },

        getCourses() {
            if (!this.onsiteTypeSelected) {
                this.filter.enrollmentStatus = null
            }
            this.pagination = {
                max: 1,
                page: 1,
                rowsPerPage: 6,
                rowsNumber: 0,
            }
            this.fetchStudentCourses()
        },
    },
}
</script>
